import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import VolumeUp from "@mui/icons-material/VolumeUp";

import { updateSection } from "../../../assignmentSlice";

import { getContentHeight } from "../../../../../utils/textUtils";
import {
  parseBlankQuestion,
  parseScrambleQuestion,
} from "../../../../../utils/parseUtils";
import { useTTS } from "../../../../../hooks/useTTS";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import ArrowButton from "../../widgets/ArrowButton";
import CorrectOverlay from "../../widgets/CorrectOverlay";
import ScramblePage from "./ScramblePage";
import EngBlankPage from "./EngBlankPage";
import KorBlankPage from "./KorBlankPage";

const SentenceTestPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const { speak, stopSpeaking } = useTTS();

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const [selectedBlankIndex, setSelectedBlankIndex] = useState(0);
  const [userInput, setUserInput] = useState("");

  const currentPage = pageList[currentIndex] || { type: "", data: {} };
  const isDictation = section.question?.is_dictation ?? true;

  const completeSentence = (question, answer, type) => {
    if (type === "eng_blank") {
      const answerList = answer.split(" ");
      return question
        .replace(/_{2,}/g, () => {
          return answerList.shift() || "______";
        })
        .split(" / ")
        .join(" ");
    } else if (type === "eng_blank_random") {
      const answerList = answer.split(" / ");
      return question
        .replace(/_{2,}/g, () => {
          return answerList.shift() || "______";
        })
        .split(" / ")
        .join(" ");
    } else if (type === "eng_scramble") {
      return question.replace(/\*\*\((.*?)\)\*\*/, answer);
    }
  };

  const refineQuestionData = (data, type) => {
    if (type === "kor_blank") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            originalQuestion: element.question,
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_blank") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            answers: element.answer.split(" "),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_blank_random") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseBlankQuestion(element.question),
            answers: element.answer.split(" / "),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    } else if (type === "eng_scramble") {
      return data.map((element) => {
        return {
          type: type,
          data: {
            ...element,
            question: parseScrambleQuestion(element.question),
            sentence: completeSentence(element.question, element.answer, type),
          },
          isSubmitted: false,
          submitCount: 0,
        };
      });
    }
    return { data: data, type: type };
  };

  useEffect(() => {
    if (currentIndex < 0) return;

    if (pageList.length > 0 && isDictation) {
      stopSpeaking();
      speak({ text: currentPage.data.sentence });
    }
    if (!currentPage.isSubmitted) {
      setSelectedBlankIndex(0);
      setUserInput("");
    } else {
      setSelectedBlankIndex(-1);
      // isSubmitted가 true이면 firstAttemptAnswer를 보여줌
      if (currentPage.data.question) {
        const firstBlank = currentPage.data.question.find(q => q.type === "blank");
        if (firstBlank && firstBlank.firstAttemptAnswer) {
          setUserInput(firstBlank.firstAttemptAnswer);
        }
      }
    }
  }, [currentIndex]);

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    let tempPageList = [];

    for (let i = 0; i < section.question.data.length; i++) {
      const { type, data } = section.question.data[i];
      if (type === "kor_blank") {
        const korBlank = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...korBlank);
      } else if (type === "eng_blank") {
        const engBlank = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engBlank);
      } else if (type === "eng_blank_random") {
        const engBlankRandom = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engBlankRandom);
      } else if (type === "eng_scramble") {
        const engScramble = refineQuestionData(data[0], type); // 3~7개의 element
        tempPageList.push(...engScramble);
      }
    }
    setPageList(tempPageList);
    setCurrentIndex(0);
  }, [section]);

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      const nextPage = pageList[currentIndex + 1];
      if (!nextPage.isSubmitted) {
        setSelectedBlankIndex(0);
      } else {
        setSelectedBlankIndex(-1);
      }
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        stopSpeaking();
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: {
                answer: pageList,
                is_finished: true,
              },
            })
          );
        }
        alert("학습을 완료하셨습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
    setUserInput("");
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevPage = pageList[currentIndex - 1];
      if (!prevPage.isSubmitted) {
        setSelectedBlankIndex(0);
      } else {
        setSelectedBlankIndex(-1);
      }
      setCurrentIndex(currentIndex - 1);
    } else {
      alert("더 이상 이전 문제가 없습니다.");
    }
    setUserInput("");
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: getContentHeight(),
          padding: "0 40px",
          fontFamily: '"Noto Sans KR", sans-serif',
          backgroundColor: "#FFFFFF",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: "80px",
            top: "100px",
            padding: "0px 10px",
            backgroundColor: "#F6F8FA",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "10px",
            boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 10px",
            cursor: "pointer",
          }}
          onClick={() => {
            stopSpeaking();
            speak({ text: currentPage.data.sentence });
          }}
        >
          <VolumeUp sx={{ fontSize: "40px", color: "#2D2D2D" }} />
        </div>
        <ArrowButton
          direction="left"
          onClick={handlePrev}
          visibleCondition={currentIndex > 0}
        />
        {/* 문제 표시 영역 */}
        <div
          style={{
            width: "80%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {currentPage.type.includes("kor_blank") && (
            <KorBlankPage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              userInput={userInput}
              selectedBlankIndex={selectedBlankIndex}
              setCurrentIndex={setCurrentIndex}
              setUserInput={setUserInput}
              setSelectedBlankIndex={setSelectedBlankIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
          {currentPage.type.includes("eng_blank") && (
            <EngBlankPage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              userInput={userInput}
              selectedBlankIndex={selectedBlankIndex}
              setCurrentIndex={setCurrentIndex}
              setUserInput={setUserInput}
              setSelectedBlankIndex={setSelectedBlankIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
          {currentPage.type.includes("scramble") && (
            <ScramblePage
              pageList={pageList}
              currentPage={currentPage}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              setPageList={setPageList}
              setOverlayVisible={setOverlayVisible}
            />
          )}
        </div>
        <ArrowButton
          direction="right"
          onClick={handleNext}
          visibleCondition={true}
        />
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay
          bool={
            currentPage.isCorrect !== undefined
              ? currentPage.isCorrect
              : currentPage.isSubmitted
          }
        />
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: "70px",
          width: "100vw",
          backgroundColor: "#F6F8FA", // 바 색상
          color: "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {currentIndex + 1} / {pageList.length}
      </div>
    </>
  );
};
export default SentenceTestPage;
